import React, { ElementType, MouseEventHandler, ReactNode } from 'react'
import { Button as MUIButton, useTheme } from '@mui/material'
import { Color } from 'themes'

import LinearProgress from '../LinearProgress'
import LinkWrapper from '../LinkWrapper'

import { useTranslation } from 'language/translator'

interface ButtonProps {
  children?: ReactNode
  className?: string
  color?:
    | Color
    | 'inherit'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'brand'
    | 'black'
    | 'white'
    | 'neutral'
  component?: ElementType<any>
  disabled?: boolean
  disableElevation?: boolean
  disableRipple?: boolean
  endIcon?: ReactNode
  fullHeight?: boolean
  fullWidth?: boolean
  href?: string
  loading?: boolean
  loadingTitle?: string
  newTab?: boolean
  onClick?: (() => void) | MouseEventHandler<HTMLElement>
  onMouseDown?: (() => void) | MouseEventHandler<HTMLElement>
  search?: string
  size?: 'small' | 'medium' | 'large' | 'gigantic'
  startIcon?: ReactNode
  sx?: object
  tabIndex?: number
  to?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'contained' | 'outlined' | 'text'
}

/**
 * The Button component allow users to take
 * actions, and make choices, with a single tap.
 */

const Button = React.forwardRef((props: ButtonProps, ref: any) => {
  const {
    className,
    loading,
    fullHeight,
    startIcon,
    endIcon,
    disabled,
    href,
    loadingTitle,
    newTab,
    search,
    size,
    sx,
    to,
    variant = 'contained',
    ...rest
  } = props

  const theme = useTheme()

  const { t } = useTranslation()

  return (
    <LinkWrapper
      disabled={disabled}
      href={to ?? href ?? undefined}
      newTab={newTab}
      search={search}
      style={props.fullWidth ? { width: '100%' } : undefined}
    >
      <MUIButton
        className={className}
        sx={{
          height: fullHeight ? '100%' : null,
          overflow: 'hidden',
          position: 'relative',
          ...(variant === 'contained'
            ? {
                bgcolor: `${props.color}.background`,
                '&:hover': {
                  bgcolor: `${props.color}.backgroundDark`
                }
              }
            : null),
          whiteSpace: 'nowrap',
          ...(size === 'gigantic' && {
            padding: '18px 32px',
            fontSize: theme.typography.h6.fontSize
          }),
          ...sx
        }}
        ref={ref}
        endIcon={loading ? null : endIcon}
        startIcon={startIcon}
        disabled={loading ? loading : disabled}
        size={size === 'gigantic' ? undefined : size}
        variant={variant}
        {...rest}
      >
        {loading
          ? `${loadingTitle ? loadingTitle : t('global', 'loading')}...`
          : props.children}

        {loading ? (
          <LinearProgress
            sx={{
              bottom: 0,
              left: 0,
              height: 2,
              position: 'absolute',
              width: '100%'
            }}
            variant="indeterminate"
          />
        ) : null}
      </MUIButton>
    </LinkWrapper>
  )
})

Button.displayName = 'Button'
export default Button
